import axios from "axios";

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5001/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor for adding token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle specific error responses
    if (error.response) {
      if (error.response.status === 401) {
        // Handle 401 Unauthorized - token expired or invalid
        localStorage.removeItem("token");
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to handle API errors
const handleApiError = (error) => {
  let errorMessage = "An unexpected error occurred";

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    errorMessage =
      error.response.data.message ||
      error.response.data.error ||
      `Error: ${error.response.status}`;
  } else if (error.request) {
    // The request was made but no response was received
    errorMessage = "No response from server. Please check your connection.";
  } else {
    // Something happened in setting up the request that triggered an Error
    errorMessage = error.message;
  }

  console.error("API Error:", errorMessage);
  return new Error(errorMessage);
};

// Auth API
export const loginUser = async (credentials) => {
  try {
    const response = await api.post("/auth/login", credentials);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const registerUser = async (userData) => {
  try {
    const response = await api.post("/auth/register", userData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await api.get("/auth/me");
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Meetings API
export const getMeetings = async (params = {}) => {
  try {
    const response = await api.get("/meetings", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getMeetingById = async (id) => {
  try {
    const response = await api.get(`/meetings/${id}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const createMeeting = async (meetingData) => {
  try {
    const response = await api.post("/meetings", meetingData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateMeeting = async (id, meetingData) => {
  try {
    const response = await api.put(`/meetings/${id}`, meetingData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateMeetingNotes = async (id, notes) => {
  try {
    const response = await api.patch(`/meetings/${id}/notes`, { notes });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const deleteMeeting = async (id) => {
  try {
    const response = await api.delete(`/meetings/${id}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateMeetingAttendance = async (id, attendance) => {
  try {
    const response = await api.patch(`/meetings/${id}/attendance`, {
      attendance,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Committees API
export const getCommittees = async () => {
  try {
    const response = await api.get("/committees");
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const createCommittee = async (committeeData) => {
  try {
    const response = await api.post("/committees", committeeData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateCommittee = async (id, committeeData) => {
  try {
    const response = await api.put(`/committees/${id}`, committeeData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const deleteCommittee = async (id) => {
  try {
    const response = await api.delete(`/committees/${id}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Members API
export const getMembers = async (params) => {
  try {
    const response = await api.get("/users", { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getMemberById = async (id) => {
  try {
    const response = await api.get(`/users/${id}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateMember = async (id, memberData) => {
  try {
    const response = await api.put(`/users/${id}`, memberData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const deleteMember = async (id) => {
  try {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// File upload and management
export const uploadFile = async (
  file,
  meetingId,
  uploader,
  description,
  retryCount = 0
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("meetingId", meetingId);
    formData.append("uploader", uploader);
    formData.append("description", description || "");

    const response = await api.post("/files/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      // Set a longer timeout for file uploads
      timeout: 30000,
    });

    return response.data;
  } catch (error) {
    // Implement retry logic (up to 2 retries)
    if (retryCount < 2 && error.message && !error.response) {
      console.log(`Retrying file upload, attempt ${retryCount + 1}`);
      return uploadFile(file, meetingId, uploader, description, retryCount + 1);
    }

    // Create a user-friendly error message
    const errorMsg = getFileErrorMessage(error);
    console.error("Error uploading file:", errorMsg);
    throw new Error(errorMsg);
  }
};

export const getFileById = async (fileId) => {
  try {
    const response = await api.get(`/files/${fileId}`);
    return response.data;
  } catch (error) {
    const errorMsg = getFileErrorMessage(error);
    console.error("Error getting file:", errorMsg);
    throw new Error(errorMsg);
  }
};

export const getFileDataUrl = (fileId) => {
  return `${api.defaults.baseURL}/files/data/${fileId}`;
};

export const getMeetingFiles = async (meetingId) => {
  try {
    const response = await api.get(`/files/meeting/${meetingId}`);
    return response.data;
  } catch (error) {
    const errorMsg = getFileErrorMessage(error);
    console.error("Error getting meeting files:", errorMsg);
    throw new Error(errorMsg);
  }
};

export const deleteFile = async (fileId) => {
  try {
    const response = await api.delete(`/files/${fileId}`);
    return response.data;
  } catch (error) {
    const errorMsg = getFileErrorMessage(error);
    console.error("Error deleting file:", errorMsg);
    throw new Error(errorMsg);
  }
};

// Helper function to get user-friendly error messages for file operations
const getFileErrorMessage = (error) => {
  if (error.response) {
    // Server responded with error
    const serverMsg =
      error.response.data?.message || error.response.data?.error;

    if (serverMsg) {
      return serverMsg;
    }

    // Handle common HTTP status codes
    switch (error.response.status) {
      case 400:
        return "Invalid file or request data";
      case 404:
        return "File not found";
      case 413:
        return "File is too large";
      case 415:
        return "File type not supported";
      default:
        return `Server error (${error.response.status})`;
    }
  }

  if (error.request) {
    // Request was made but no response received
    return "No response from server. Please check your connection.";
  }

  // Something else happened
  return error.message || "An unknown error occurred with the file";
};

// Link management endpoints
export const getMeetingLinks = async (meetingId) => {
  try {
    const response = await api.get(`/meetings/${meetingId}/links`);
    return response.data;
  } catch (error) {
    console.error("Error getting meeting links:", error);
    throw handleApiError(error);
  }
};

export const addMeetingLink = async (meetingId, linkData) => {
  try {
    const response = await api.post(`/meetings/${meetingId}/links`, linkData);
    return response.data;
  } catch (error) {
    console.error("Error adding link:", error);
    throw handleApiError(error);
  }
};

export const deleteMeetingLink = async (meetingId, linkId) => {
  try {
    const response = await api.delete(`/meetings/${meetingId}/links/${linkId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting link:", error);
    throw handleApiError(error);
  }
};

export default api;
